import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'

export default function GetStartedPage() {
  return (
    <>
      <SEO
        title="Get Started - WealthSpace"
        description="Choose how you'd like to experience WealthSpace. Start instantly with our self-guided tour or book a personalised demo with our team."
        keywords="WealthSpace demo, get started, financial adviser software trial, book demo, instant start"
        url="/get-started"
      />

      {/* Hero */}
      <section className="py-20 px-6 max-w-[800px] mx-auto text-center relative z-[1]">
        <p className="text-sm font-semibold text-[#14b8a6] tracking-[0.1em] uppercase mb-4 animate-fade-in-up opacity-0 [animation-delay:0.1s] [animation-fill-mode:forwards]">
          Experience WealthSpace
        </p>
        <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.2] tracking-[-0.02em] mb-5 animate-fade-in-up opacity-0 [animation-delay:0.15s] [animation-fill-mode:forwards]">
          How would you like to <span className="text-[#14b8a6]">get started</span>?
        </h1>
        <p className="text-lg text-[#a8a5a0] leading-[1.6] max-w-[550px] mx-auto animate-fade-in-up opacity-0 [animation-delay:0.2s] [animation-fill-mode:forwards]">
          Jump straight in with our self-guided tour, or book a personalised demo with our team.
        </p>
      </section>

      {/* Choice Cards */}
      <section className="px-6 pb-[100px] relative z-[1]">
        <div className="max-w-[900px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Instant Start Card - Coming Soon */}
          <div className="relative animate-fade-in-up opacity-0 [animation-delay:0.25s] [animation-fill-mode:forwards]">
            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-brand-dark/70 backdrop-blur-[2px] rounded-2xl z-10" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-[#0f1318] text-lg font-bold py-3 px-8 rounded-lg uppercase tracking-[0.1em] z-20 shadow-[0_8px_32px_rgba(245,158,11,0.4)] whitespace-nowrap">
              Coming Soon
            </div>

            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl py-10 px-8 text-center h-full flex flex-col items-center">
              {/* Icon */}
              <div className="relative w-[72px] h-[72px] rounded-2xl bg-gradient-to-br from-[rgba(20,184,166,0.15)] to-[rgba(16,185,129,0.08)] border border-[rgba(20,184,166,0.3)] flex items-center justify-center mb-6">
                <span className="absolute -top-2 -right-2 bg-gradient-to-br from-[#14b8a6] to-[#10b981] text-[#0f1318] text-[10px] font-bold py-1 px-2.5 rounded-[20px] uppercase tracking-[0.05em]">
                  Fastest
                </span>
                <svg
                  className="w-8 h-8 text-[#14b8a6]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>

              <h3 className="font-serif text-[26px] font-medium tracking-[-0.01em] mb-3">
                Instant Start
              </h3>
              <p className="text-[15px] text-[#a8a5a0] leading-[1.6] mb-6">
                Watch a quick video, answer a few questions, and get immediate access to explore
                WealthSpace yourself.
              </p>

              {/* Features */}
              <ul className="text-left w-full mb-8">
                {[
                  '15-minute guided video walkthrough',
                  'Quick knowledge check (4 questions)',
                  'FCA verification & instant login',
                  '7-day free trial',
                ].map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-sm text-[#c4c2bc] py-2 border-b border-white/[0.04] last:border-0"
                  >
                    <svg
                      className="w-[18px] h-[18px] text-[#14b8a6] flex-shrink-0 mt-[1px]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto">
                <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-gradient-to-br from-[#14b8a6] to-[#10b981] text-[#0f1318] font-semibold text-[15px]">
                  Start Now
                  <svg
                    className="w-[18px] h-[18px]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <p className="text-[13px] text-[#6b6965] mt-4 flex items-center justify-center gap-1.5">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Ready in ~20 minutes
                </p>
              </div>
            </div>
          </div>

          {/* Book a Call Card */}
          <Link
            to="/contact"
            className="group bg-white/[0.03] border border-white/[0.08] rounded-2xl py-10 px-8 text-center flex flex-col items-center transition-all duration-400 hover:bg-white/[0.06] hover:border-white/15 hover:-translate-y-1 relative overflow-hidden animate-fade-in-up opacity-0 [animation-delay:0.3s] [animation-fill-mode:forwards]"
          >
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#8b5cf6] to-transparent opacity-0 transition-opacity duration-400 group-hover:opacity-100" />

            {/* Icon */}
            <div className="w-[72px] h-[72px] rounded-2xl bg-gradient-to-br from-[rgba(139,92,246,0.15)] to-[rgba(167,139,250,0.08)] border border-[rgba(139,92,246,0.3)] flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-[#a78bfa]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>

            <h3 className="font-serif text-[26px] font-medium tracking-[-0.01em] mb-3">
              Book a Call
            </h3>
            <p className="text-[15px] text-[#a8a5a0] leading-[1.6] mb-6">
              Schedule a personalised demo with our team. We'll tailor the session to your
              practice's specific needs.
            </p>

            {/* Features */}
            <ul className="text-left w-full mb-8">
              {[
                '30-minute 1-on-1 video call',
                'Tailored to your workflow',
                'Live Q&A with product expert',
                'Integration & migration guidance',
              ].map((feature, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-sm text-[#c4c2bc] py-2 border-b border-white/[0.04] last:border-0"
                >
                  <svg
                    className="w-[18px] h-[18px] text-[#a78bfa] flex-shrink-0 mt-[1px]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="mt-auto">
              <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-gradient-to-br from-[#8b5cf6] to-[#a78bfa] text-[#0f1318] font-semibold text-[15px] transition-transform duration-300 group-hover:scale-105">
                Choose a Time
                <svg
                  className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
              <p className="text-[13px] text-[#6b6965] mt-4 flex items-center justify-center gap-1.5">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Next available: Tomorrow
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* Demo Link */}
      <div className="text-center px-6 pb-[60px] relative z-[1] animate-fade-in-up opacity-0 [animation-delay:0.35s] [animation-fill-mode:forwards]">
        <p className="text-[15px] text-[#6b6965]">
          Already familiar with WealthSpace?{' '}
          <Link
            to="/demo"
            className="text-[#14b8a6] font-medium hover:text-[#2dd4bf] transition-colors hover:underline"
          >
            Try our interactive demo
          </Link>
        </p>
      </div>
    </>
  )
}
